import React from 'react';
import InboxSidebar from './sidebar/InboxSidebar';
import isNil from 'lodash/isNil';
import MailActions from 'actions/inbox/MailActions';
import TagActions from 'actions/TagActions';
import FolderActions from 'actions/inbox/FolderActions';
import getFilters from './utils/getFilters';
import Loading from 'components/pure/utils/Loading';
import urlParams from 'utils/urlParams';
import FolderStore from 'stores/inbox/FolderStore';
import MailStore from 'stores/inbox/MailStore';
import connectToStores from 'alt-utils/lib/connectToStores';
import {browserHistory, Link} from 'react-router';
import './InboxPage.styl';
import Button from '@material-ui/core/Button';
import confirm from 'utils/confirm';
import ToastStore from '@store/toast/ToastStore';
import FolderSource from 'sources/inbox/FolderSource';
import classNames from 'classnames';
import {getQuery, getQueryParam} from 'utils/getQueryParam';
import {RouterLocation} from '@store/@types/util/ReactRouter';
import Folder, {FolderId} from 'records/Folder';

interface InboxPageProps {
  location: RouterLocation;
}

@connectToStores
export default class InboxPage extends React.PureComponent<
  InboxPageProps & ReturnType<typeof InboxPage.getPropsFromStores>
> {
  static getStores() {
    return [FolderStore, MailStore];
  }

  static getPropsFromStores() {
    const {mail} = MailStore.getState();
    const {folders, isLoading, wasError} = FolderStore.getState();
    // Can only be undefined while we're loading or if there's an error -- componentDidMount awaits on the folder fetch
    // call, and every user has an inbox.
    const inboxFolderId = folders.find(f => f.isInbox())?.id;

    return {
      mail,
      folders,
      inboxFolderId,
      isLoading,
      wasError,
    };
  }

  async componentDidMount() {
    const folders = await FolderActions.fetchFolders();
    const filters = getFilters(getQuery(this.props.location));

    if (isNil(filters.folderId) && !this.isTrendIntermediatePage() && !this.isFromEmail()) {
      // Don't use props.inboxFolderId here -- we literally just got the folders back from the call above. Use those
      // instead.
      this.selectFolder(folders.find(folder => folder.isInbox()).id);
    }
  }

  // this very specifically is to fix a single issue -
  // when navigating to the inbox header FROM inside the inbox UI, we dont
  // invoke initialize because we're already mounted. but we do have folders,
  // so detect this and set it.
  componentDidUpdate() {
    const {pathname} = this.props.location;
    const query = getQuery(this.props.location);
    const filters = getFilters(query);

    if (isNil(filters.folderId) && pathname === '/inbox/items') {
      this.selectFolder(this.props.inboxFolderId);
    }
  }

  componentWillUnmount() {
    // TODO Resolve tag collision issue
    // tags from a folder other than Inbox can cause troubles when we get back to this page
    TagActions.clearTags.defer();
  }

  isTrendIntermediatePage = () => {
    const path = this.props.location.pathname;
    return path.startsWith('/inbox/trends');
  };

  isFromEmail() {
    return getQueryParam(this.props.location, 'fromEmail');
  }

  selectFolder = (folderId: FolderId | undefined) => {
    const filters = getFilters(getQuery(this.props.location));
    if (filters.folderId !== folderId || filters.viewAll) {
      let newFilters = filters.merge({folderId, viewAll: false}).delete('tagId');

      // If we're going to the inbox, set some defaults.
      if (folderId === this.props.inboxFolderId) {
        newFilters = newFilters.set('viewAll', true);
      }

      TagActions.clearTags.defer();
      MailActions.clearMailForFilters.defer(newFilters);
      browserHistory.replace('/inbox/items' + urlParams(newFilters));
      document.documentElement.scrollTop = 0;
    }
  };

  onEmptyTrash = () => {
    FolderSource.emptyTrash().then(() => {
      ToastStore.addToast({
        type: 'success',
        message: 'Deleted items in trash',
      });
      const query = getQuery(this.props.location);
      MailActions.clearMailForFilters.defer(getFilters(query));
      MailActions.fetchMail(getFilters(query), 0);
    });
  };

  renderHeader = (folder: Folder | undefined) => {
    return !folder || folder.isInbox() ? (
      <header>
        <h1>Inbox</h1>
        <Button
          className="create-smart-alert-button"
          component={Link}
          to="/admin/actions/create"
          variant="outlined"
          color="primary">
          Create Smart Alert
        </Button>
      </header>
    ) : (
      <header className={classNames(folder.isTrash() && 'trash')}>
        <h1>{folder.name}</h1>
        {folder.isTrash() && (
          <Button
            variant="contained"
            color="primary"
            onClick={() =>
              confirm('Are you sure you want to delete all items in the trash?', 'Empty Trash?')
                .then(this.onEmptyTrash)
                .catch(console.error)
            }>
            Empty Trash
          </Button>
        )}
      </header>
    );
  };

  render() {
    const {folders, isLoading, wasError} = this.props;
    const filters = getFilters(getQuery(this.props.location));
    const folder = folders.find(folder => folder.id === filters.folderId);
    return isLoading && folders.size === 0 ? (
      <Loading />
    ) : wasError ? (
      <div className="inbox-page">
        <p className="content ev-error-message">Error loading inbox.</p>
      </div>
    ) : (
      <div className="inbox-page">
        <InboxSidebar
          folders={folders}
          filters={filters}
          onSelectFolder={this.selectFolder}
          location={location}
        />
        <main className="content">
          {!this.isTrendIntermediatePage() && this.renderHeader(folder)}
          {this.props.children}
        </main>
      </div>
    );
  }
}
